import { useState, useEffect } from "react";
import { Autocomplete, TextField, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { brokerApi, IPafApplication } from "../../_api";
import { useAuth } from "../../_auth/useAuth";
import { UseFormSetValue } from "react-hook-form";
import { useBrokerAutoFill } from "./useBrokerAutoFill";

export type BrokerCreationContext = 'paf' | 'listadmin';

export type BrokerSelectionProps = {
    setValue: UseFormSetValue<IPafApplication>
    onSaveBeforeNavigate?: (navigationPath: string, navigationState?: any) => Promise<void>;
    pafId?: string;
    customerId?: number;
    context?: BrokerCreationContext; // Context from which broker creation is initiated
}

export const BrokerSelection = ({ setValue, onSaveBeforeNavigate, pafId, customerId, context = 'paf' }: BrokerSelectionProps) => {
    const { getAccessToken } = useAuth();
    const navigate = useNavigate();
    const { autoFillBrokerById } = useBrokerAutoFill(setValue);
    const [brokerOptions, setBrokerOptions] = useState<Array<{ value: number | 'create'; label: string }>>([]);
    const [selItem, setSelItem] = useState<number | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);

    // Fetch all brokers on mount
    useEffect(() => {
        const fetchAllBrokers = async () => {
            setLoading(true);
            const auth_token = await getAccessToken();
            // Use specified payload for brokerApi.get
            const resp = await brokerApi.get_broker_list(auth_token);
            if (resp && resp.success && resp.data) {
                const brokers = resp.data.map(({ listadmin_id, listadmin_sid, company_name, email }: any) => ({ value: listadmin_id, label: `${listadmin_sid} | ${company_name ?? ''} | ${email}` }));
                setBrokerOptions([
                    { value: 'create', label: 'Add your Broker' },
                    ...brokers
                ]);
            } else {
                setBrokerOptions([{ value: 'create', label: 'Add your Broker' }]);
            }
            setLoading(false);
        };
        fetchAllBrokers();
    }, [getAccessToken]);

    // Handle broker selection
    const handleBrokerSelect = async (value: number | undefined) => {
        setSelItem(value);
        if (value !== undefined) {
            await autoFillBrokerById(value);
        }
    };

    // Handle broker creation
    const handleCreateBroker = async () => {
        const navigationState = {
            brokerCreationContext: context,
            brokerCreationPafId: pafId,
            brokerCreationCustomerId: customerId
        };
        if (onSaveBeforeNavigate) {
            await onSaveBeforeNavigate('/listadmin/new', navigationState);
        } else {
            navigate('/listadmin/new', { state: navigationState });
        }
    };

    return (
        <>
            <div style={{ marginBottom: 16 }}>
                <Autocomplete
                    size="medium"
                    fullWidth
                    options={brokerOptions}
                    getOptionLabel={(option: { value: number | 'create'; label: string }) => option.label}
                    loading={loading}
                    value={brokerOptions.find(opt => opt.value === selItem) || null}
                    onChange={(_e: React.SyntheticEvent, value: { value: number | 'create'; label: string } | null) => {
                        if (value?.value === 'create') {
                            handleCreateBroker();
                        } else {
                            handleBrokerSelect(value?.value as number);
                        }
                    }}
                    isOptionEqualToValue={(option: { value: number | 'create'; label: string }, value: { value: number | 'create'; label: string } | undefined) => value === undefined || option.value === value?.value}
                    ListboxProps={{
                        style: { maxHeight: 300, overflowY: 'auto' },
                        children: undefined
                    }}
                    renderOption={(props: object, option: { value: number | 'create'; label: string }) => {
                        if (option.value === 'create') {
                            return (
                                <Box 
                                    component="li" 
                                    {...props} 
                                    key={option.value}
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 1,
                                        py: 1
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleCreateBroker();
                                    }}
                                >
                                    <Box component="span" sx={{ color: 'text.secondary' }}>
                                        Can't find?
                                    </Box>
                                    <Box
                                        component="span"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleCreateBroker();
                                        }}
                                        sx={{
                                            color: 'primary.main',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                color: 'primary.dark'
                                            }
                                        }}
                                    >
                                        {option.label}
                                    </Box>
                                </Box>
                            );
                        }
                        return (
                            <Box component="li" {...props} key={option.value}>
                                {option.label}
                            </Box>
                        );
                    }}
                    renderInput={(params: any) =>
                        <TextField {...params} label="Authorized Broker" variant="standard" />
                    }
                    noOptionsText={null}
                />
            </div>
        </>
    );
}
